/* Copyright (c) 2020-present CloudIO Inc */

import { DataGrid } from 'componentregistry';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useMemo } from 'react';
import { useUserSetup } from '../../../root/useUser';
import { LIST_ID } from '../../DataGrid/grid/state';
import { DataContext } from '../../DataGrid/grid/types';
import { useCellStyles } from '../../DataGrid/styles';
import Grid from './Grid';
import { ListProps } from './types';

const columns: Required<DataGrid.StringColumn.StringColumnItem>[] = [
  {
    type: 'StringColumn' as const,
    itemId: 'xx',
    parent: 'x',
    children: [],
    props: DataGrid.StringColumn.defaultValue,
  },
];

let counter = 0;

const List = React.forwardRef(
  (
    {
      CellComponent,
      custom,
      height = 300,
      onClick,
      rowHeight = 36,
      scrollToColumn = 0,
      scrollToRow = 0,
      showBorder = false,
      showRowLines = true,
      totalRows,
      width,
      bodyRef,
      bodyProps,
      onRowDoubleClick,
      itemId = null,
      listStore = null,
      queryOnNext = false,
      selectField = false,
    }: ListProps,
    ref: React.Ref<HTMLDivElement>,
  ): React.ReactElement => {
    const platform = usePlatform();
    const userSetup = useUserSetup();
    const cellClasses = useCellStyles();

    const dataContext = useMemo<DataContext>(
      () => ({
        pageId: LIST_ID,
        itemId: itemId || `list-${++counter}`,
        headerRows: 0,
        showFilters: false,
        transparentBG: false,
        platform,
        stripeRows: false,
        totalColumns: 1,
        highlightActiveRow: true,
        headerBackgroundColor: 'transparent',
        headerColor: '',
        userSetup,
        cellClasses,
        roleUid: 'all_users',
        selectField: selectField,
      }),
      [cellClasses, itemId, platform, userSetup, selectField],
    );

    return (
      <Grid
        CellComponent={CellComponent}
        appUid="x"
        roleUid="x"
        backgroundColor="paper"
        bodyProps={bodyProps}
        bodyRef={bodyRef}
        columnBuffer={0}
        columnWidth={() => width}
        columns={columns}
        custom={custom}
        dataContext={dataContext}
        expandLastColumn
        fixedLeftColumns={0}
        fixedRightColumns={0}
        footerRows={0}
        headerRows={0}
        height={height}
        highlightRow
        itemId={dataContext.itemId}
        listStore={listStore}
        onRowDoubleClick={onRowDoubleClick}
        pageId={LIST_ID}
        queryOnNext={queryOnNext}
        ref={ref}
        rowBuffer={2}
        rowHeight={rowHeight}
        scrollToColumn={scrollToColumn}
        scrollToRow={scrollToRow}
        showBorder={showBorder}
        showColumnLines={false}
        showRowLines={showRowLines}
        totalColumns={1}
        totalRows={totalRows}
        width={width}
        highlightCell={false}
        onClick={onClick}
      />
    );
  },
);

List.displayName = 'List';

export default List;
